import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRewards, useRewardsHistory } from "@/api/hooks/use-query-api";
import type { CosmopointsConnection } from "@/api/models/api-types";
import { ErrorState } from "@/components/common/error-state";
import { RewardsOverviewSection } from "./sections/RewardsOverviewSection";
import { RewardsHistorySection } from "./sections/RewardsHistorySection";
import { LoyaltyProgramsSection } from "./sections/LoyaltyProgramsSection";
import { ConvertRewardsDialog } from "./ConvertRewardsDialog";
import { LinkProgramDialog } from "./LinkProgramDialog";
import { useCosmopointsEnabled } from "./hooks/use-cosmopoints-enabled";

const HISTORY_PAGE_SIZE = 10;

/**
 * Which conversion the open dialog is for: Harmony credits, a specific linked
 * program's points, or nothing (closed).
 */
type ConvertTarget = "credits" | CosmopointsConnection | null;

export const RewardsTab: React.FC = () => {
  const { t } = useTranslation();
  const [convertTarget, setConvertTarget] = useState<ConvertTarget>(null);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [page, setPage] = useState(1);
  const { isEnabled: isCosmopointsEnabled } = useCosmopointsEnabled();

  const {
    data: rewardsData,
    isLoading: isBalancesLoading,
    error: balancesError,
    refetch: refetchBalances,
  } = useRewards();

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = useRewardsHistory({ page, limit: HISTORY_PAGE_SIZE });

  if (isBalancesLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
            {t("settings.rewards.title")}
          </h2>
          <div className="h-24 bg-gray-50 dark:bg-gray-700 rounded-xl animate-pulse" />
        </div>
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
            {t("settings.rewards.history")}
          </h2>
          <div className="h-40 bg-gray-50 dark:bg-gray-700 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (balancesError || !rewardsData?.data) {
    return (
      <ErrorState
        title={t("settings.rewards.loadError")}
        description={t("settings.rewards.loadErrorDescription")}
        onRetry={() => refetchBalances()}
      />
    );
  }

  const { credits } = rewardsData.data;

  return (
    <div className="flex flex-col gap-8">
      <RewardsOverviewSection
        credits={credits}
        onConvert={() => setConvertTarget("credits")}
      />

      {isCosmopointsEnabled && (
        <LoyaltyProgramsSection
          credits={credits}
          onLink={() => setIsLinkOpen(true)}
          onConvert={setConvertTarget}
        />
      )}

      <RewardsHistorySection
        items={historyData?.data?.result ?? []}
        meta={historyData?.data?.meta}
        isLoading={isHistoryLoading}
        isError={isHistoryError}
        page={page}
        onPageChange={setPage}
      />

      <ConvertRewardsDialog
        open={convertTarget !== null}
        onClose={() => setConvertTarget(null)}
        availableCredits={credits}
        connection={
          convertTarget && convertTarget !== "credits"
            ? convertTarget
            : undefined
        }
      />

      {isCosmopointsEnabled && (
        <LinkProgramDialog
          open={isLinkOpen}
          onClose={() => setIsLinkOpen(false)}
        />
      )}
    </div>
  );
};
